﻿import React, { CSSProperties, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';

import { Button, ButtonProps, Input, Space, Tooltip} from 'antd';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/pro-solid-svg-icons';

import Map from 'ol/Map';
import Control from 'ol/control/Control';
import { easeOut } from 'ol/easing';

export interface ZoomButtonProps extends Omit<ButtonProps, 'icon' | 'onClick'> {
    iconZoomIn?: React.ReactNode,
    iconZoomOut?: React.ReactNode,
}

const ZOOM_DURATION = 250;

export interface Props {
    map: Map | undefined,
    id?: string,
    className?: string,
    style?: CSSProperties,
    buttonProps?: ZoomButtonProps,    
    onChange?: (zoom: number, animation_was_cancelled?: boolean) => void,
    mobile?: boolean
}

const MapZoomControl: React.FC<Props> = ({
    map,
    id,
    className = "map-zoom-control",
    style,
    onChange = () => {},
    buttonProps = {} as ZoomButtonProps,   
    mobile,   
}) => {

    const element = useRef<HTMLDivElement>(null);
    const container = useRef<HTMLDivElement>(null);

    const {
        type = "primary",
        size, // = "small",
        iconZoomIn = <FontAwesomeIcon icon={faPlus} fixedWidth />,
        iconZoomOut = <FontAwesomeIcon icon={faMinus} fixedWidth />,
        ...rest
    } = buttonProps; 
   
    useEffect(() => {
        if (map && element.current && container.current) {

            const control = new Control({
                target: container.current,
                element: element.current,                    
            })

            map.addControl(control);

            return () => {               
                try {                    
                    map.removeControl(control);
                }
                catch { }                
            }                   
        }
    }, [map]);    

    const zoom = (adjustment: number) => {
        const view = map?.getView();
        if (view) {
            const currentZoom = view.getZoom();
            if (currentZoom !== undefined) {
                const newZoom = view.getConstrainedZoom(currentZoom + adjustment);
                if (newZoom !== undefined && newZoom !== currentZoom) {
                    if (ZOOM_DURATION > 0) {
                        if (view.getAnimating())
                            view.cancelAnimations();

                        view.animate({
                            zoom: newZoom,
                            duration: ZOOM_DURATION,
                            easing: easeOut
                        }, (x) => {
                            onChange(newZoom, !x);
                        });
                    }
                    else {
                        view.setZoom(newZoom);
                        onChange(newZoom);
                    }
                }
            }
        }
    }
    
    return (        
        <div id={id} className={className} ref={container}
            style={{
                pointerEvents: 'auto',
                position: 'absolute',
                ...style            
            }}
        >
            <div ref={element}>           

                <Space direction='vertical' size={-1} style={style}>
                    <Tooltip title={mobile ? '' : "Zoom in"} placement="right">
                        <Button
                            type={type} size={size}
                            icon={iconZoomIn}
                            onClick={() => zoom(1)}
                            {...rest}
                            style={{
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                                ...rest.style
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={mobile ? '' : "Zoom out"} placement="right">
                        <Button                            
                            type={type} size={size}
                            icon={iconZoomOut}                            
                            onClick={() => zoom(-1)}
                            {...rest}
                            style={{
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                                ...rest.style
                            }}                           
                        />
                    </Tooltip>
                </Space>                
            </div>
        </div>
    )  
   
}

export default observer(MapZoomControl);
